/**
 * "Change password" — `POST /api/auth/forgot-password` (PIVOT-347), on the
 * profile page between `DownloadData` and `SignOutEverywhere`. A signed-in
 * password user gets the reset link mailed to their own session address, so
 * they no longer have to sign out and hunt for the link on the login page.
 *
 * No new route and no current-password form: the existing forgot-password
 * route already binds the token to the current hash, is single use, and
 * answers `204` whatever it found (no user enumeration). The reset page it
 * mails is the one thing that actually sets the password.
 *
 * Rendered only for an account that has a password. That comes from
 * `user.has_password`, which the server computes from the stored hash — never
 * from the absence of a Google sign-in, because a Google account can hold a
 * password too and that proxy fails silently.
 */

import { useState } from 'react'
import { api } from '../api'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n'
import './ChangePassword.css'

export function ChangePassword() {
  const { t } = useI18n()
  const { user } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(false)

  if (!user?.has_password) return null
  const { email } = user

  const handleClick = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(false)
    setSent(false)
    try {
      await api.auth.forgotPassword(email)
    } catch {
      // Nothing was mailed, so claiming success here would be a fabricated
      // one (constitution §I). The user can press again.
      setError(true)
      setSubmitting(false)
      return
    }
    setSent(true)
    setSubmitting(false)
  }

  return (
    <section className="change-password">
      <h3>{t('profile.changePasswordTitle')}</h3>
      <p className="change-password-body">{t('profile.changePasswordBody')}</p>
      {sent && <p className="form-success">{t('profile.changePasswordSent', { email })}</p>}
      {error && <p className="form-error">{t('profile.changePasswordFailed')}</p>}
      <button
        type="button"
        className="btn btn-outline"
        disabled={submitting}
        aria-busy={submitting}
        onClick={() => void handleClick()}
      >
        {submitting ? t('common.loading') : t('profile.changePasswordButton')}
      </button>
    </section>
  )
}
